import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Button, Spinner, Alert, Form, Row, Col, Table, Modal } from 'react-bootstrap';
import { fetchMailingListRequest, createMailingListRequest, updateMailingListRequest, deleteMailingListRequest, clearMailingListError } from '../../store/actions/mailingListActions';
import { FiMail, FiUser, FiBriefcase, FiRefreshCw, FiPlus, FiTrash2, FiEdit } from 'react-icons/fi';
import { useTranslation } from '../../hooks/useTranslation';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminMailingList.css';

const AdminMailingList = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { mailingList, create: createState, update: updateState, delete: deleteState } = useSelector((state) => state.mailingList);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        department: '',
    });
    const [successMessage, setSuccessMessage] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(fetchMailingListRequest());
    }, [dispatch]);

    useEffect(() => {
        if (createState.success) {
            setSuccessMessage(t('pages.adminMailingList.createSuccess'));
            setFormData({
                email: '',
                first_name: '',
                last_name: '',
                department: '',
            });
            setErrors({});
            setShowModal(false);
            setIsEditMode(false);
            // Le saga gère déjà le rafraîchissement avec forceRefresh
            // Pas besoin de refetch manuel ici
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [createState.success]);

    useEffect(() => {
        if (updateState.success) {
            setSuccessMessage(t('pages.adminMailingList.updateSuccess'));
            setFormData({
                email: '',
                first_name: '',
                last_name: '',
                department: '',
            });
            setErrors({});
            setShowEditModal(false);
            setIsEditMode(false);
            setSelectedEntry(null);
            // Le saga gère déjà le rafraîchissement avec forceRefresh
            // Pas besoin de refetch manuel ici
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [updateState.success]);

    useEffect(() => {
        if (deleteState.success) {
            setSuccessMessage(t('pages.adminMailingList.deleteSuccess'));
            setShowDeleteModal(false);
            setSelectedEntry(null);
            // Le saga gère déjà le rafraîchissement avec forceRefresh
            // Pas besoin de refetch manuel ici
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [deleteState.success]);

    // Rafraîchir automatiquement en cas d'erreur pour restaurer l'état correct
    useEffect(() => {
        if (createState.error) {
            // Le saga gère déjà le rafraîchissement en cas d'erreur
            // Pas besoin de refetch manuel ici
        }
    }, [createState.error]);

    useEffect(() => {
        if (deleteState.error) {
            // Le saga gère déjà le rafraîchissement en cas d'erreur
            // Pas besoin de refetch manuel ici
        }
    }, [deleteState.error]);

    const handleOpenModal = () => {
        setIsEditMode(false);
        setFormData({
            email: '',
            first_name: '',
            last_name: '',
            department: '',
        });
        setErrors({});
        dispatch(clearMailingListError());
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setIsEditMode(false);
        setFormData({
            email: '',
            first_name: '',
            last_name: '',
            department: '',
        });
        setErrors({});
        dispatch(clearMailingListError());
    };

    const handleOpenEditModal = (entry) => {
        setIsEditMode(true);
        setSelectedEntry(entry);
        setFormData({
            email: entry.email || '',
            first_name: entry.first_name || '',
            last_name: entry.last_name || '',
            department: entry.department || '',
        });
        setErrors({});
        dispatch(clearMailingListError());
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setIsEditMode(false);
        setSelectedEntry(null);
        setFormData({
            email: '',
            first_name: '',
            last_name: '',
            department: '',
        });
        setErrors({});
        dispatch(clearMailingListError());
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.email.trim()) {
            newErrors.email = t('pages.adminMailingList.validation.emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            newErrors.email = t('pages.adminMailingList.validation.emailInvalid');
        }

        if (!formData.first_name.trim()) {
            newErrors.first_name = t('pages.adminMailingList.validation.firstNameRequired');
        }

        if (!formData.last_name.trim()) {
            newErrors.last_name = t('pages.adminMailingList.validation.lastNameRequired');
        }

        if (!formData.department.trim()) {
            newErrors.department = t('pages.adminMailingList.validation.departmentRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        if (e) {
            e.preventDefault();
        }
        
        if (!validateForm()) {
            return;
        }

        const mailingListData = {
            email: formData.email.trim(),
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            department: formData.department.trim(),
        };

        if (isEditMode && selectedEntry) {
            const emailId = selectedEntry.email_id || selectedEntry.id;
            if (emailId) {
                dispatch(updateMailingListRequest(emailId, mailingListData));
            }
        } else {
            dispatch(createMailingListRequest(mailingListData));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        // Effacer l'erreur pour ce champ quand l'utilisateur commence à taper
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null,
            }));
        }
        // Effacer l'erreur API si l'utilisateur modifie le formulaire
        if (createState.error || updateState.error) {
            dispatch(clearMailingListError());
        }
    };

    const handleDeleteClick = (entry) => {
        setSelectedEntry(entry);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedEntry(null);
    };

    const handleConfirmDelete = () => {
        if (selectedEntry) {
            const emailId = selectedEntry.email_id || selectedEntry.id;
            if (emailId) {
                dispatch(deleteMailingListRequest(emailId));
            }
        }
    };

    return (
        <div className="dashboard-page admin-mailing-list-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiMail className="me-2" />
                                {t('pages.adminMailingList.title')}
                            </h2>
                            <p className="text-muted mb-0">{t('pages.adminMailingList.subtitle')}</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button 
                                variant="outline-primary" 
                                onClick={() => dispatch(fetchMailingListRequest())}
                                disabled={mailingList.loading}
                            >
                                <FiRefreshCw className={mailingList.loading ? 'spinning' : ''} /> {t('common.refresh')}
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={handleOpenModal}
                            >
                                <FiPlus /> {t('pages.adminMailingList.add')}
                            </Button>
                        </div>
                    </div>

                    {successMessage && (
                        <Alert variant="success" className="mt-3">
                            {successMessage}
                        </Alert>
                    )}

                    {createState.error && (
                        <Alert variant="danger" className="mt-3" dismissible onClose={() => {
                            // L'erreur sera effacée lors du prochain submit ou refresh
                        }}>
                            <Alert.Heading>{t('pages.adminMailingList.errorCreate')}</Alert.Heading>
                            <p className="mb-0">{createState.error}</p>
                        </Alert>
                    )}

                    {updateState.error && (
                        <Alert variant="danger" className="mt-3" dismissible onClose={() => {
                            // L'erreur sera effacée lors du prochain submit ou refresh
                        }}>
                            <Alert.Heading>{t('pages.adminMailingList.errorUpdate')}</Alert.Heading>
                            <p className="mb-0">{updateState.error}</p>
                        </Alert>
                    )}

                    {/* Tableau de la mailing list */}
                    <Card className="mt-4">
                        <Card.Header>
                            <h5 className="mb-0">{t('pages.adminMailingList.listTitle')}</h5>
                        </Card.Header>
                        <Card.Body>
                            {mailingList.error && (
                                <Alert variant="danger" className="mb-3">
                                    {mailingList.error}
                                </Alert>
                            )}

                            {mailingList.loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-3">{t('pages.adminMailingList.loading')}</p>
                                </div>
                            ) : (() => {
                                // Extraire les données en gérant différents formats de réponse
                                // Le reducer devrait déjà avoir normalisé les données, mais on double-vérifie
                                const rawData = mailingList.data;
                                let mailingListArray = [];
                                
                                if (Array.isArray(rawData)) {
                                    mailingListArray = rawData;
                                } else if (rawData && typeof rawData === 'object') {
                                    // Gérer différents formats de réponse si le reducer n'a pas normalisé
                                    mailingListArray = rawData.data || rawData.records || rawData.items || rawData.results || [];
                                    if (!Array.isArray(mailingListArray)) {
                                        mailingListArray = [];
                                    }
                                } else {
                                    mailingListArray = [];
                                }

                                if (mailingListArray.length === 0) {
                                    return (
                                        <div className="text-center py-5">
                                            <FiMail size={48} className="text-muted mb-3" />
                                            <p className="text-muted">{t('pages.adminMailingList.noEntries')}</p>
                                        </div>
                                    );
                                }

                                return (
                                    <>
                                        <div className="table-responsive table-scroll">
                                            <Table hover striped className="admin-table">
                                                <thead>
                                                    <tr>
                                                        <th>{t('pages.adminMailingList.email')}</th>
                                                        <th>{t('pages.adminMailingList.firstName')}</th>
                                                        <th>{t('pages.adminMailingList.lastName')}</th>
                                                        <th>{t('pages.adminMailingList.department')}</th>
                                                        <th className="text-end">{t('common.actions')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {mailingListArray.map((entry, index) => (
                                                        <tr key={entry.email_id || entry.id || `entry-${index}`}>
                                                            <td>{entry.email || 'N/A'}</td>
                                                            <td>{entry.first_name || 'N/A'}</td>
                                                            <td>{entry.last_name || 'N/A'}</td>
                                                            <td>
                                                                <span className="badge bg-info">
                                                                    {entry.department || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td className="text-end">
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    className="me-2"
                                                                    onClick={() => handleOpenEditModal(entry)}
                                                                    disabled={updateState.loading || deleteState.loading}
                                                                    title={t('common.edit')}
                                                                    aria-label="Modifier"
                                                                >
                                                                    <FiEdit />
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleDeleteClick(entry)}
                                                                    disabled={updateState.loading || deleteState.loading}
                                                                    title={t('common.delete')}
                                                                    aria-label="Supprimer"
                                                                >
                                                                    <FiTrash2 />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                        <div className="mt-3 text-muted text-center">
                                            {t('pages.adminMailingList.total', { count: mailingListArray.length })}
                                        </div>
                                    </>
                                );
                            })()}
                        </Card.Body>
                    </Card>

                    {/* Modal d'ajout */}
                    <Modal show={showModal} onHide={handleCloseModal} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiPlus className="me-2" />
                                {t('pages.adminMailingList.modalAddTitle')}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {createState.error && (
                                <Alert variant="danger" className="mb-3" dismissible onClose={() => {
                                    // L'erreur sera effacée lors du prochain submit
                                }}>
                                    <Alert.Heading>{t('pages.adminMailingList.errorCreate')}</Alert.Heading>
                                    <p className="mb-0">{createState.error}</p>
                                </Alert>
                            )}
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FiMail className="me-2" />
                                                {t('pages.adminMailingList.email')} *
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder={t('pages.adminMailingList.placeholder.email')}
                                                value={formData.email}
                                                onChange={handleChange}
                                                isInvalid={!!errors.email}
                                                disabled={createState.loading}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.email}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FiBriefcase className="me-2" />
                                                {t('pages.adminMailingList.department')} *
                                            </Form.Label>
                                            <Form.Select
                                                name="department"
                                                value={formData.department}
                                                onChange={handleChange}
                                                isInvalid={!!errors.department}
                                                disabled={createState.loading}
                                                required
                                            >
                                                <option value="">{t('pages.adminMailingList.selectDepartment')}</option>
                                                <option value="admin + tech">admin + tech</option>
                                                <option value="tech">tech</option>
                                                <option value="admin">admin</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.department}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FiUser className="me-2" />
                                                {t('pages.adminMailingList.firstName')} *
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="first_name"
                                                placeholder={t('pages.adminMailingList.placeholder.firstName')}
                                                value={formData.first_name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.first_name}
                                                disabled={createState.loading}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.first_name}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FiUser className="me-2" />
                                                {t('pages.adminMailingList.lastName')} *
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="last_name"
                                                placeholder={t('pages.adminMailingList.placeholder.lastName')}
                                                value={formData.last_name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.last_name}
                                                disabled={createState.loading}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.last_name}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={handleCloseModal}
                                disabled={createState.loading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={createState.loading}
                            >
                                {createState.loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        {t('pages.adminMailingList.adding')}
                                    </>
                                ) : (
                                    <>
                                        <FiMail className="me-2" />
                                        {t('pages.adminMailingList.addToList')}
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Modal d'édition */}
                    <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiEdit className="me-2" />
                                {t('pages.adminMailingList.modalEditTitle')}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {updateState.error && (
                                <Alert variant="danger" className="mb-3" dismissible onClose={() => {
                                    // L'erreur sera effacée lors du prochain submit
                                }}>
                                    <Alert.Heading>{t('pages.adminMailingList.errorUpdate')}</Alert.Heading>
                                    <p className="mb-0">{updateState.error}</p>
                                </Alert>
                            )}
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FiMail className="me-2" />
                                                {t('pages.adminMailingList.email')} *
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder={t('pages.adminMailingList.placeholder.email')}
                                                value={formData.email}
                                                onChange={handleChange}
                                                isInvalid={!!errors.email}
                                                disabled={updateState.loading}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.email}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FiBriefcase className="me-2" />
                                                {t('pages.adminMailingList.department')} *
                                            </Form.Label>
                                            <Form.Select
                                                name="department"
                                                value={formData.department}
                                                onChange={handleChange}
                                                isInvalid={!!errors.department}
                                                disabled={updateState.loading}
                                                required
                                            >
                                                <option value="">{t('pages.adminMailingList.selectDepartment')}</option>
                                                <option value="admin + tech">admin + tech</option>
                                                <option value="tech">tech</option>
                                                <option value="admin">admin</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.department}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FiUser className="me-2" />
                                                {t('pages.adminMailingList.firstName')} *
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="first_name"
                                                placeholder={t('pages.adminMailingList.placeholder.firstName')}
                                                value={formData.first_name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.first_name}
                                                disabled={updateState.loading}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.first_name}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <FiUser className="me-2" />
                                                {t('pages.adminMailingList.lastName')} *
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="last_name"
                                                placeholder={t('pages.adminMailingList.placeholder.lastName')}
                                                value={formData.last_name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.last_name}
                                                disabled={updateState.loading}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.last_name}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={handleCloseEditModal}
                                disabled={updateState.loading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={updateState.loading}
                            >
                                {updateState.loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        {t('pages.adminMailingList.updating')}
                                    </>
                                ) : (
                                    <>
                                        <FiEdit className="me-2" />
                                        {t('pages.adminMailingList.update')}
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Modal de confirmation de suppression */}
                    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                        <Modal.Header closeButton>
                            <Modal.Title>{t('pages.adminMailingList.confirmDelete')}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {deleteState.error && (
                                <Alert variant="danger" className="mb-3">
                                    {deleteState.error}
                                </Alert>
                            )}
                            {selectedEntry && (
                                <p>
                                    {t('pages.adminMailingList.deleteMessage')}
                                </p>
                            )}
                            {selectedEntry && (
                                <div className="bg-light p-3 rounded">
                                    <p className="mb-1"><strong>{t('pages.adminMailingList.email')} :</strong> {selectedEntry.email}</p>
                                    <p className="mb-1"><strong>{t('pages.adminMailingList.firstName')} :</strong> {selectedEntry.first_name}</p>
                                    <p className="mb-1"><strong>{t('pages.adminMailingList.lastName')} :</strong> {selectedEntry.last_name}</p>
                                    <p className="mb-0"><strong>{t('pages.adminMailingList.department')} :</strong> {selectedEntry.department}</p>
                                </div>
                            )}
                            <p className="mt-3 text-danger">
                                <strong>{t('pages.adminMailingList.irreversible')}</strong>
                            </p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={handleCloseDeleteModal}
                                disabled={deleteState.loading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleConfirmDelete}
                                disabled={deleteState.loading}
                            >
                                {deleteState.loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        {t('pages.adminMailingList.deleting')}
                                    </>
                                ) : (
                                    <>
                                        <FiTrash2 className="me-2" />
                                        {t('common.delete')}
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminMailingList;
